import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeart,
  faBookmark,
  faStar,
  faPlay,
  faBrain,
  faClone,
  faClock,
  faPen,
  faTrash,
  faPenToSquare,
  faLock,
  faCircleCheck,
  faGlobe,
  faUpload,
  faEyeSlash,
  faEye,
  faArrowsRotate,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import './Flashcards.css';

const DIFFICULTY = {
  1: { label: 'Beginner', cls: 'fc-badge-beginner' },
  2: { label: 'Intermediate', cls: 'fc-badge-intermediate' },
  3: { label: 'Advanced', cls: 'fc-badge-advanced' },
};

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString();
};

// 10-star display driven by a 0–10 average.
const StarRow = ({ value }) => {
  const filled = Math.round(value || 0);
  return (
    <span className="fc-stars" aria-label={`${(value || 0).toFixed(1)} out of 10`}>
      {Array.from({ length: 10 }).map((_, i) => (
        <FontAwesomeIcon
          key={i}
          icon={faStar}
          className={i < filled ? '' : 'fc-star-empty'}
        />
      ))}
    </span>
  );
};

const DeckCard = ({
  deck,
  entitlement,
  onStartStudy,
  onOpenStats,
  onOpenReview,
  onLockedReview,
  onEdit,
  onDelete,
  onToggleFavorite,
  onToggleBookmark,
  onPublish,
  onUnlist,
  onRelist,
  onSwitchVersion,
}) => {
  const difficulty = DIFFICULTY[deck.difficulty] || DIFFICULTY[1];
  const cardCount = deck.cardCount || 0;
  const learned = deck.learnedCount || 0;
  const progress = cardCount > 0 ? Math.round((learned / cardCount) * 100) : 0;
  const lastStudied = formatDate(deck.lastStudiedAt);
  const hasStudied = !!deck.lastStudiedAt || learned > 0;
  const dueCount = deck.dueCount || 0;
  const newCount = deck.newCount || 0;
  const nothingToStudy = dueCount + newCount === 0;
  const canReview = entitlement && entitlement.canReview;
  const hasReviews = (deck.reviewCount || 0) > 0;

  // Publish-as-snapshot: only the owner's editable working deck (never a
  // snapshot itself — published versions can't be edited/deleted/republished).
  // "My Decks" also returns bookmarked public decks (isSnapshot: true,
  // isOwner: false), so every owner-only affordance hangs off this flag.
  const isOwnerWorkingDeck = deck.isOwner && !deck.isSnapshot;
  const isPublished = !!deck.publishedDeckId;
  // Shown on any snapshot the user is studying — including one they authored
  // and then bookmarked, which now surfaces under "My Decks".
  const showNewerVersionBanner =
    !isOwnerWorkingDeck && deck.isSnapshot && deck.hasNewerVersion;

  const handleReviewClick = () => {
    if (canReview) onOpenReview(deck);
    else onLockedReview(deck);
  };

  const handlePublishClick = () => {
    if (!deck.canPublish) return;
    onPublish(deck);
  };

  return (
    <div className="fc-card">
      {deck.isFavorite && (
        <span className="fc-card-fav-star" title="Favorite">
          <FontAwesomeIcon icon={faStar} />
        </span>
      )}

      <div className="fc-card-top">
        <span
          className="fc-color-dot"
          style={{ backgroundColor: deck.color || '#6366f1' }}
        />
        <h3 className="fc-card-name">{deck.name}</h3>

        <div className="fc-card-toggles">
          <button
            type="button"
            className={`fc-icon-toggle ${deck.isFavorite ? 'fc-fav-on' : ''}`}
            title={deck.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            aria-pressed={deck.isFavorite}
            onClick={() => onToggleFavorite(deck)}
          >
            <FontAwesomeIcon icon={faHeart} />
          </button>
          <button
            type="button"
            className={`fc-icon-toggle ${deck.inStudyList ? 'fc-bookmark-on' : ''}`}
            title={deck.inStudyList ? 'Remove from study list' : 'Add to study list'}
            aria-pressed={deck.inStudyList}
            onClick={() => onToggleBookmark(deck)}
          >
            <FontAwesomeIcon icon={faBookmark} />
          </button>
        </div>

        <span className={`fc-badge ${difficulty.cls}`}>{difficulty.label}</span>
      </div>

      {deck.description && <p className="fc-card-desc">{deck.description}</p>}

      <div className="fc-card-meta">
        <span>
          <FontAwesomeIcon icon={faClone} /> {cardCount} cards
        </span>
        <span>
          <FontAwesomeIcon icon={faClock} />{' '}
          {lastStudied ? `Last studied: ${lastStudied}` : 'Not studied yet'}
        </span>
        {isOwnerWorkingDeck && isPublished && (
          <span className="fc-published-badge">
            <FontAwesomeIcon icon={faGlobe} /> Published · v{deck.publishedVersion}
          </span>
        )}
      </div>

      {showNewerVersionBanner && (
        <div className="fc-newer-version-banner">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>A newer version of this deck is available.</span>
          <button
            type="button"
            className="fc-btn fc-btn-ghost fc-btn-sm"
            onClick={() => onSwitchVersion(deck)}
          >
            <FontAwesomeIcon icon={faArrowsRotate} /> Switch to newest version
          </button>
        </div>
      )}

      {isOwnerWorkingDeck && (
        <div className="fc-owner-actions">
          <button
            type="button"
            className="fc-btn fc-btn-ghost fc-btn-sm"
            onClick={() => onEdit(deck)}
          >
            <FontAwesomeIcon icon={faPen} /> Edit
          </button>
          <button
            type="button"
            className="fc-btn fc-btn-danger fc-btn-sm"
            onClick={() => onDelete(deck)}
          >
            <FontAwesomeIcon icon={faTrash} /> Delete
          </button>
          <button
            type="button"
            className="fc-btn fc-btn-ghost fc-btn-sm"
            onClick={handlePublishClick}
            disabled={!deck.canPublish}
            title={
              !deck.canPublish
                ? `Publishing requires at least 50 cards (this deck has ${cardCount}).`
                : undefined
            }
          >
            <FontAwesomeIcon icon={faUpload} /> {isPublished ? 'Publish update' : 'Make public'}
          </button>
          {isPublished && (
            <button
              type="button"
              className="fc-btn fc-btn-ghost fc-btn-sm"
              onClick={() => (deck.unlisted ? onRelist(deck) : onUnlist(deck))}
            >
              <FontAwesomeIcon icon={deck.unlisted ? faEye : faEyeSlash} />{' '}
              {deck.unlisted ? 'Relist' : 'Unlist'}
            </button>
          )}
        </div>
      )}

      <div className="fc-rating-box">
        <div className="fc-rating-info">
          {hasReviews ? (
            <>
              <StarRow value={deck.averageRating} />
              <span className="fc-rating-value">
                {(deck.averageRating || 0).toFixed(1)}
              </span>
              <span className="fc-rating-count">
                ({deck.reviewCount} {deck.reviewCount === 1 ? 'review' : 'reviews'})
              </span>
            </>
          ) : (
            <span className="fc-no-rating">No reviews yet</span>
          )}
        </div>
        <button
          type="button"
          className="fc-btn fc-btn-success fc-btn-review"
          onClick={handleReviewClick}
        >
          {!canReview && <FontAwesomeIcon icon={faLock} />}
          {deck.myRating ? (
            <>
              <FontAwesomeIcon icon={faPenToSquare} /> Update Review
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faStar} /> Write Review
            </>
          )}
        </button>
      </div>

      {hasStudied && cardCount > 0 && (
        <div className="fc-progress-wrap">
          <div className="fc-progress-track">
            <div className="fc-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="fc-progress-label">{progress}% complete</span>
        </div>
      )}

      {!nothingToStudy && (
        <div className="fc-count-badges">
          {dueCount > 0 && (
            <span className="fc-count-badge fc-count-due">{dueCount} due</span>
          )}
          {newCount > 0 && (
            <span className="fc-count-badge fc-count-new">{newCount} new</span>
          )}
        </div>
      )}

      <div className="fc-card-actions">
        <button
          type="button"
          className="fc-btn fc-btn-primary fc-btn-block"
          disabled={nothingToStudy}
          onClick={() => onStartStudy(deck)}
        >
          {nothingToStudy ? (
            <>
              <FontAwesomeIcon icon={faCircleCheck} /> All caught up
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPlay} />{' '}
              {hasStudied ? 'Resume Studying' : 'Start Studying'}
            </>
          )}
        </button>

        {hasStudied && (
          <button
            type="button"
            className="fc-btn fc-btn-success fc-btn-block"
            onClick={() => onOpenStats(deck)}
          >
            <FontAwesomeIcon icon={faBrain} /> My Stats
          </button>
        )}
      </div>
    </div>
  );
};

export default DeckCard;
